import { error, misc, success } from "app/themes/colors";
import EnumDataSource from "utils/enum-data-source.util";

import { AssignedTag } from "./assigned-tag.type";
import { Clinic } from "./clinics.type";

export interface SendFaxInput {
  recipient: string;
  documents?: any;
  clinic: number;
  patient?: number;
  box_file_ids?: number | string;
  include_cover_page?: boolean;
  notes?: string;
  subject?: string;
  recipient_name?: string;
  sender_name?: string;
}

export interface FaxesQueryParams {
  recipient_name?: string;
  sender?: number;
  query?: string;
  patient?: number;
  sender_name?: string;
  assigned_tags?: number | number[];
  before_date?: string;
  after_date?: string;
  clinic?: number | number[];
  search?: string;
  deactivated?: boolean;
  blocked?: boolean;
  direction?: number;
  status?: number;
  // ENG-2511: the Unassigned queue (no patient) and its unread count.
  unassigned?: boolean;
  read?: boolean;
}

export interface FaxFilters extends FaxesQueryParams {
  limit?: number;
  offset?: number;
  ordering?: string;
}

export interface FaxMessage {
  messageId?: string;
  deliveryId?: string | null;
  watermark?: string | null;
  status?: string;
  pagesCount?: number | null;
  pagesComplete?: number | null;
  duration?: number | null;
  faxNumber?: string;
  faxCsid?: string | null;
  faxCallerId?: string;
  faxECM?: number | null;
  faxSpeed?: number | null;
  faxDetected?: boolean | null;
  faxProtocol?: number | null;
  faxAttempt?: number | null;
  direction?: string;
  channelType?: string | null;
  recipientName?: string | null;
  subject?: string | null;
  accountId?: string | null;
  errorCode?: string | null;
  errorInfo?: string | null;
  resultCode?: string | null;
  resultInfo?: string | null;
  isArchived?: boolean;
  isFilePurged?: boolean;
  country?: string | null;
  createdAt?: string | null;
  resolvedDate?: string | null;
  deletedAt?: string | null;
}

export interface FaxAttachment {
  id: number;
  name: string;
  content_type?: string;
  order?: number;
  file: string;
}

/**
 * Freshly signed URLs for one fax, from `GET /faxes/{id}/document-urls/`.
 *
 * Separate from the URLs on `FaxResponse` on purpose: those are signed when the list response
 * is built and expire an hour later, so they go stale in a tab left open. These are minted on
 * request and must be used immediately rather than stored.
 */
export interface FaxDocumentUrls {
  id: number;
  document: string | null;
  attachments: FaxAttachment[];
}

/** Which of a fax's files a preview is showing. */
export type FaxDocumentSource = "document" | "attachments";

export interface FaxResponse {
  id: number;
  /** Only requested by the Fax Center when the unassigned-queue flag is on. Outbound faxes are null. */
  read?: boolean | null;
  created_at: string;
  sender?: {
    id: number;
    first_name: string;
    last_name: string;
    name: string;
    full_name: string;
    suffix: string;
    title: number;
    is_active: boolean;
    photo: string;
    signature: string;
  };
  clinic: Clinic;
  recipient: string;
  webhook_id: string;
  message: FaxMessage;
  documents?: string[];
  box_file_ids?: string[];
  patient?: {
    id: number;
    first_name: string;
    last_name: string;
    box_folder_id?: string;
  };
  include_cover_page?: boolean;
  cover_page_id?: string | null;
  notes?: string;
  subject?: string;
  recipient_name?: string;
  sender_name?: string;
  document: string;
  attachments?: FaxAttachment[];
  status: FaxStatus | null;
  assigned_tags?: AssignedTag[];
  direction: number;
  discarded_at?: null | string;
  deactivated_at?: string | null;
  blocked_at?: string | null;
  deactivated_by?: {
    id: number;
    first_name: string;
    last_name: string;
    name: string;
  } | null;
  // Failure detail. Only present when the fax has failed, and only when the request asked for it
  // (see getFaxesQuery) — an older backend does not have these fields at all.
  failure_code?: string;
  failure_category?: FaxFailureCategory | "";
  // Resolved server-side from failure_code rather than mapped here on purpose: the queue, the
  // receipt dialog, the in-app notification and the failure email all read the same sentences,
  // so the wording cannot drift between them.
  failure_reason?: string;
  failure_guidance?: string;
  send_attempt_count?: number;
  last_attempted_at?: string | null;
  // Auto-retry cycle bookkeeping. Only present when the request asked for it (FAX_AUTO_RETRY_FEATURE
  // gates the query keys the same way the failure fields above are gated). auto_retry_count counts
  // automatic retries fired in the CURRENT cycle — a manual resend resets it; next_retry_at is when
  // the next automatic retry fires, null when none is scheduled.
  auto_retry_count?: number;
  next_retry_at?: string | null;
}

/**
 * What the reader should do about a failure — not what went wrong technically.
 *
 * Mirrors FaxFailureCategory in the backend's business_logic/fax_failure_codes.py. Only the
 * category is duplicated here (to pick a colour); the sentences are served by the API.
 */
export enum FaxFailureCategory {
  /** The far end was unavailable. Sending again is likely to work. */
  Temporary = "temporary",
  /** Something has to change first, usually the number. Resending as-is fails the same way. */
  Permanent = "permanent",
  /** Some pages already arrived — resending delivers the whole document a second time. */
  Partial = "partial",
  /** No code, or one we don't recognise. Says so rather than guessing. */
  Unknown = "unknown"
}

export const FAX_FAILURE_CATEGORY_COLORS: Record<FaxFailureCategory, string> = {
  [FaxFailureCategory.Temporary]: misc.darkYellow,
  [FaxFailureCategory.Permanent]: error[400],
  [FaxFailureCategory.Partial]: misc.warningText,
  [FaxFailureCategory.Unknown]: misc.grayAsphalt
};

export const FAX_FAILURE_CATEGORY_LABELS: Record<FaxFailureCategory, string> = {
  [FaxFailureCategory.Temporary]: "Temporary · try again",
  [FaxFailureCategory.Permanent]: "Check the number first",
  [FaxFailureCategory.Partial]: "Partly delivered",
  [FaxFailureCategory.Unknown]: "Reason unknown"
};

export enum FaxStatus {
  "Processing" = 1,
  "Cancelled",
  "Success",
  "Failed"
}

export const FAX_STATUS_COLORS: Record<FaxStatus, string> = {
  [FaxStatus.Processing]: misc.darkYellow,
  [FaxStatus.Success]: success[600],
  [FaxStatus.Failed]: error[400],
  [FaxStatus.Cancelled]: misc.warningText
};

const faxStatuses = {
  [FaxStatus.Success]: "Success",
  [FaxStatus.Processing]: "Processing",
  [FaxStatus.Failed]: "Failed",
  [FaxStatus.Cancelled]: "Cancelled"
};

export const FAXES_STATUSES = new EnumDataSource(faxStatuses);

export const FAXES_DIRECTION_OUTBOUND = 1;
export const FAXES_DIRECTION_INBOUND = 2;

export const FAXES_DIRECTIONS = new EnumDataSource({
  [FAXES_DIRECTION_INBOUND]: "Inbound",
  [FAXES_DIRECTION_OUTBOUND]: "Outbound"
});
